/**
 * SHE Quick Guide - Floating Contextual Helper
 * 
 * A floating button that provides instant, page-specific guidance
 * without requiring AI interaction. Shows quick tips, next steps,
 * and navigation suggestions based on the current page.
 * 
 * Complements the SHE Chat Widget (AI-powered) with quick, actionable tips.
 */

import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Compass, X, ChevronRight, Lightbulb, ArrowRight, 
  BookOpen, Users, Target, Heart, Video, Sparkles,
  MessageSquare, Settings, Star, RefreshCw, Flame
} from 'lucide-react';

// Page-specific guidance content
const PAGE_GUIDANCE = {
  '/dashboard': {
    title: 'Your Home Base',
    tips: [
      { icon: Target, text: "Set your focus intention to get personalized guidance", action: '/dashboard', highlight: true },
      { icon: BookOpen, text: "Start your day with a journal entry", action: '/journal' },
      { icon: Users, text: "Connect with your sisters in the community", action: '/communities' }
    ],
    encouragement: "You showed up today. That's the first victory.",
    nextStep: { label: "Set Your Focus", href: '/dashboard' }
  },
  '/communities': {
    title: 'Sister Circles',
    tips: [
      { icon: Heart, text: "Find a circle that matches your focus area", action: '/communities' },
      { icon: MessageSquare, text: "Introduce yourself — your sisters are waiting", action: '/communities' },
      { icon: Star, text: "Look for 'For You' badges on recommended circles", action: '/communities' }
    ],
    encouragement: "Connection is medicine. You don't have to do this alone.",
    nextStep: { label: "Join a Circle", href: '/communities' }
  },
  '/journal': {
    title: 'Soul Journal',
    tips: [
      { icon: BookOpen, text: "Use voice recording for stream-of-consciousness entries", action: '/journal' },
      { icon: Sparkles, text: "Ask SHE to reflect on your entry for deeper insights", action: '/journal' },
      { icon: Heart, text: "Your words are sacred — there's no wrong way to journal", action: '/journal' }
    ],
    encouragement: "What you write here stays between you and your becoming.",
    nextStep: { label: "Write an Entry", href: '/journal' }
  },
  '/videos': {
    title: 'The Vault',
    tips: [
      { icon: Video, text: "Start with 'Getting Started' if you're new", action: '/videos' },
      { icon: Target, text: "Filter by your focus area for relevant content", action: '/videos' },
      { icon: Heart, text: "Save videos to your collection for later", action: '/videos' }
    ],
    encouragement: "Wisdom is here when you're ready to receive it.",
    nextStep: { label: "Explore The Vault", href: '/videos' }
  },
  '/truth-reset': {
    title: 'Truth Reset Exercise',
    tips: [
      { icon: RefreshCw, text: "This exercise helps you release limiting beliefs", action: '/truth-reset' },
      { icon: Lightbulb, text: "Be radically honest — no one sees this but you", action: '/truth-reset' },
      { icon: Sparkles, text: "Complete all steps for the full transformation", action: '/truth-reset' }
    ],
    encouragement: "The truth you've been avoiding is the doorway to your freedom.",
    nextStep: { label: "Begin Reset", href: '/truth-reset' }
  },
  '/legacy-vision': {
    title: 'YUMMY Declaration',
    tips: [
      { icon: Compass, text: "Start by selecting your core values", action: '/legacy-vision' },
      { icon: Target, text: "Your 10-year vision is your North Star", action: '/legacy-vision' },
      { icon: Sparkles, text: "Use 'Ask SHE to refine' for your legacy statement", action: '/legacy-vision' }
    ],
    encouragement: "Your legacy is being written with every choice you make today.",
    nextStep: { label: "Map Your Vision", href: '/legacy-vision' }
  },
  '/challenges': {
    title: '6-Week Challenges',
    tips: [
      { icon: Flame, text: "Pick a challenge that stretches you, not breaks you", action: '/challenges' },
      { icon: Users, text: "Find an accountability partner for better results", action: '/circles' },
      { icon: Star, text: "Complete daily check-ins to stay on track", action: '/challenges' }
    ],
    encouragement: "Six weeks of focused effort can change everything.",
    nextStep: { label: "Start a Challenge", href: '/challenges' }
  },
  '/sister-spotlights': {
    title: 'Sister Spotlights',
    tips: [
      { icon: Star, text: "Read other sisters' stories for inspiration", action: '/sister-spotlights' },
      { icon: Heart, text: "Nominate a sister who's inspired you", action: '/sister-spotlights' },
      { icon: Lightbulb, text: "Share your own transformation story", action: '/sister-spotlights' }
    ],
    encouragement: "Every sister here has a story worth celebrating — including yours.",
    nextStep: { label: "Explore Spotlights", href: '/sister-spotlights' }
  },
  '/circles': {
    title: 'Accountability Circles',
    tips: [
      { icon: Users, text: "Small circles create deeper connections", action: '/circles' },
      { icon: Target, text: "Share your weekly wins and challenges", action: '/circles' },
      { icon: Heart, text: "What's shared in the circle stays in the circle", action: '/circles' }
    ],
    encouragement: "Accountability is love made visible.",
    nextStep: { label: "Find Your Circle", href: '/circles' }
  },
  '/messages': {
    title: 'Messages',
    tips: [
      { icon: MessageSquare, text: "Reach out to a sister you've connected with", action: '/messages' },
      { icon: Heart, text: "Don't wait for the perfect words — just say hello", action: '/messages' },
      { icon: Users, text: "Check the directory to find sisters with similar interests", action: '/directory' }
    ],
    encouragement: "A message can change someone's day. Send one.",
    nextStep: { label: "Send a Message", href: '/messages' }
  },
  '/settings': {
    title: 'Your Settings',
    tips: [
      { icon: Settings, text: "Update your profile photo so sisters recognize you", action: '/settings' },
      { icon: Heart, text: "Set your focus area for personalized recommendations", action: '/settings' },
      { icon: Star, text: "Check your Diamond Points balance", action: '/settings' }
    ],
    encouragement: "Taking care of your account is taking care of yourself.",
    nextStep: { label: "Update Profile", href: '/settings' }
  },
  // Default for unmatched pages
  default: {
    title: 'Your Journey',
    tips: [
      { icon: Target, text: "Head to your dashboard for personalized guidance", action: '/dashboard' },
      { icon: Users, text: "Connect with sisters in the community", action: '/communities' },
      { icon: BookOpen, text: "Process your thoughts in your journal", action: '/journal' }
    ],
    encouragement: "Every step forward counts. You're exactly where you need to be.",
    nextStep: { label: "Go to Dashboard", href: '/dashboard' }
  }
};

const SHEQuickGuide = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [guidance, setGuidance] = useState(null);
  const [hasSeenTip, setHasSeenTip] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasInnerCircleAccess } = useAuth();

  // Only show for authenticated members with tier access
  const shouldShow = user && hasInnerCircleAccess && hasInnerCircleAccess();

  useEffect(() => {
    // Update guidance based on current page
    const path = location.pathname;
    const matchedGuidance = PAGE_GUIDANCE[path] || PAGE_GUIDANCE.default;
    setGuidance(matchedGuidance);
    
    // Show pulse animation for new pages (first 5 seconds)
    setHasSeenTip(false);
    const timer = setTimeout(() => setHasSeenTip(true), 5000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  if (!shouldShow || !guidance) return null;

  const handleTipClick = (action) => {
    if (action && action !== location.pathname) {
      navigate(action);
    }
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating Guide Button - Left side, above SHE Chat */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-[9998] transition-all duration-300 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        }`}
        data-testid="she-quick-guide-btn"
        title="Quick Guide"
      >
        <div className={`relative h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg flex items-center justify-center hover:scale-110 transition-transform ${
          !hasSeenTip ? 'animate-pulse' : ''
        }`}>
          <Compass className="h-6 w-6 text-white" />
          {!hasSeenTip && (
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-terracotta rounded-full flex items-center justify-center">
              <Lightbulb className="h-2.5 w-2.5 text-white" />
            </span>
          )}
        </div>
      </button>

      {/* Guide Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-[9998] w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          data-testid="she-quick-guide-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-500 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                  <Compass className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Quick Guide</h3>
                  <p className="text-white/80 text-xs">{guidance.title}</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
          </div>

          {/* Tips */}
          <div className="p-4 space-y-2">
            {guidance.tips.map((tip, index) => (
              <button
                key={index}
                onClick={() => handleTipClick(tip.action)}
                className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left ${
                  tip.highlight 
                    ? 'bg-purple-50 hover:bg-purple-100 border border-purple-200' 
                    : 'hover:bg-gray-50'
                }`}
                data-testid={`quick-guide-tip-${index}`}
              >
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  tip.highlight ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <tip.icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${tip.highlight ? 'text-purple-900 font-medium' : 'text-gray-700'}`}>
                    {tip.text}
                  </p>
                </div>
                <ChevronRight className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                  tip.highlight ? 'text-purple-500' : 'text-gray-400'
                }`} />
              </button>
            ))}
          </div>

          {/* Encouragement */}
          <div className="px-4 pb-3">
            <p className="text-xs text-gray-500 italic text-center">
              "{guidance.encouragement}"
            </p>
          </div>

          {/* Next Step CTA */}
          <div className="p-3 bg-gray-50 border-t border-gray-100">
            <Button
              onClick={() => handleTipClick(guidance.nextStep.href)}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm"
              data-testid="quick-guide-next-step"
            >
              {guidance.nextStep.label}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default SHEQuickGuide;
